import i18next from "i18next";

import { Models } from "@/api/types";

type SignupRewardDiscountType =
  | "PercentOff"
  | "AmountOff"
  | "FreeItemSingle"
  | "FreeItemAll"
  | "FreeItemChooseFrom"
  | "InOffice";

export const getSignupRewardDiscountType = (
  activeSignupRewardData?: Models<"DisplayActiveReward"> | null,
): SignupRewardDiscountType => {
  if (activeSignupRewardData?.discount?.amountOff) {
    return "AmountOff";
  }
  if (activeSignupRewardData?.discount?.percentOff) {
    return "PercentOff";
  }
  if (activeSignupRewardData?.freePackageGroup?.hasSelectableInclusions) {
    return "FreeItemChooseFrom";
  }

  if (activeSignupRewardData?.freePackageGroup?.freePackageGroupInclusions?.length === 1) {
    return "FreeItemSingle";
  }

  if (activeSignupRewardData?.freePackageGroup) {
    return "FreeItemAll";
  }

  return "InOffice";
};

export const getSignupRewardInclusions = (
  activeSignupRewardData: Models<"DisplayActiveReward"> | null,
  listings: Models<"ListingModel">[],
): Models<"ListingModel">[] => {
  if (!activeSignupRewardData) {
    return [];
  }

  const discountType = getSignupRewardDiscountType(activeSignupRewardData);

  // Case 1: discount && amount off - use requirements
  if (discountType === "AmountOff") {
    const requirements = activeSignupRewardData.discount?.discountRequirements;
    if (requirements && requirements.length > 0) {
      return listings.filter((listing) =>
        listing.packages?.some((pkg) =>
          requirements.some((req: { packageId?: string | null }) => req.packageId === pkg.id),
        ),
      );
    }
  }

  // Case 2: discount && percent off - use inclusions
  if (discountType === "PercentOff") {
    const inclusions = activeSignupRewardData?.discount?.discountInclusions;
    if (inclusions && inclusions.length > 0) {
      return listings.filter((listing) =>
        listing.packages?.some((pkg) =>
          inclusions.some((inc: { packageId?: string | null }) => inc.packageId === pkg.id),
        ),
      );
    }
  }

  // Case 3: free item - use free package group
  if (
    discountType === "FreeItemAll" ||
    discountType === "FreeItemSingle" ||
    discountType === "FreeItemChooseFrom"
  ) {
    const freePackageGroupInclusions =
      activeSignupRewardData.freePackageGroup?.freePackageGroupInclusions;
    if (freePackageGroupInclusions && freePackageGroupInclusions.length > 0) {
      return listings.filter((listing) =>
        listing.packages?.some((pkg) =>
          freePackageGroupInclusions.some(
            (inc: { packageId?: string | null }) => inc.packageId === pkg.id,
          ),
        ),
      );
    }
  }

  return [];
};

export const getSignupRewardTitle = (signupReward: Models<"DisplayActiveReward">) => {
  const signupRewardDiscountType = getSignupRewardDiscountType(signupReward);

  const amountOff = Number(signupReward.discount?.amountOff ?? 0) / 100;
  const discountPercentOff = Number(signupReward.discount?.percentOff ?? 0) * 100;

  if (signupRewardDiscountType === "AmountOff") {
    return i18next.t("rewardsUtils.amountOff", { amountOff });
  }

  if (signupRewardDiscountType === "PercentOff") {
    return i18next.t("rewardsUtils.percentOff", { discountPercentOff });
  }

  if (signupRewardDiscountType === "FreeItemAll") {
    return i18next.t("rewardsUtils.freeItems");
  }

  if (signupRewardDiscountType === "FreeItemSingle") {
    return i18next.t("rewardsUtils.aFreeItem");
  }

  if (signupRewardDiscountType === "FreeItemChooseFrom") {
    return i18next.t("rewardsUtils.chooseFromFreeItems");
  }

  return i18next.t("rewardsUtils.aReward");
};

export const getValidInclusionIds = (
  packagesFromListings: Array<{ id: string }>,
  freePackageGroupInclusions?: Array<{
    membershipId?: string | null;
    packageId?: string | null;
    id: string;
  }> | null,
): string[] => {
  if (!freePackageGroupInclusions) {
    return [];
  }

  return freePackageGroupInclusions
    .filter((inclusion) => {
      return (
        inclusion.membershipId ||
        (inclusion.packageId && packagesFromListings.some((pkg) => pkg.id === inclusion.packageId))
      );
    })
    .map((i) => i.id);
};
